import { useAppSelector } from "features/store";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";

type PlatformRouteProps = {
  children: ReactNode;
  /** Restrict to developers (integration registration = lifecycle tier). */
  requireDeveloper?: boolean;
};

/**
 * Gate for the integration-platform UI (CAL-5832): any internal platform role
 * (developer / superuser / calibrate manager) may view; routes flagged
 * `requireDeveloper` (the register wizard) additionally need the developer
 * role. Mirrors the backend tiers in integration_platform/api/core/
 * permissions.py - the API enforces its own permissions regardless.
 */
const PlatformRoute = ({
  children,
  requireDeveloper = false,
}: PlatformRouteProps): JSX.Element => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const pp = user?.platform_profile;

  if (user && !user.is_pass_reset && !(pp && !pp.is_pass_reset)) {
    return <Navigate to="/reset-password" replace />;
  }

  const notArchived = !!pp && !pp.is_archived;
  const canView =
    notArchived &&
    (!!pp.is_developer || pp.is_superuser || pp.is_calibrate_manager);
  const allowed = requireDeveloper
    ? notArchived && !!pp.is_developer
    : canView;

  if (!user || !allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default PlatformRoute;
